import type { PropertyValues, TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { EntityContent } from 'src/content/entity-content.js';
import 'src/library/elements/d-section';
import 'src/library/editors/elements/d-edit-text';
import 'src/library/editors/elements/d-edit-textarea';
import 'src/library/elements/d-wrap';
import 'src/library/elements/d-action';
import 'src/library/lists/d-list-section';
import 'src/library/editors/elements/d-select-dropdown';
import 'src/library/editors/elements/d-select-date';
import '../../library/editors/components/d-edit-timespan';
import 'src/library/elements/d-smooth-resize';
import 'src/library/elements/d-popover-bubble';
import 'src/library/fields/d-closer';

import { customElement, property, state } from 'lit/decorators.js';
import { capitalize, isEmptyOrInvalidString, numberWord, uuid } from 'src/utilities/text';

import {
  getEventItemsList,
  getFilterParams,
  getItemsAsCollectionEntities,
  getItemsList,
} from 'src/store/selectors/collections';
import { LocalDate } from 'src/utilities/local-date';
import { RecurringEventInCollectionDialog } from 'src/content/collections/recurring-event-in-collection-dialog';
import { allEqual } from 'src/store/selectors';
import _ from 'lodash';
import { employeeInterviewTemplateId } from 'src/store/selectors/templates';

import type { UpdateSectionItem } from '../update-section-item';
import type { CollectionView } from './collection-view';
import type { RecurringEventInCollectionDialogResult } from './recurring-event-in-collection-dialog-result';
import type { CollectionItem } from 'src/store/selectors/collectionItem';
import type { CollectionSuggestionClickData } from 'src/store/selectors/collectionSuggestionClickData';
import type { CollectionFilter } from 'src/store/selectors/collectionFilter';
import type { CollectionPdf } from 'src/pdf/collection-pdf';
import type { CollectionEntity } from 'src/store/selectors/collectionEntity';
import type { CollectionViewEditItem } from './collection-view-edit-item';
import type { ListSectionItemInput } from 'src/library/lists/listSectionItemInput';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import type { CreateEventOccurrenceInput } from 'src/layout/parts/create-event-occurrence-input';
import type { CreateEntityInput } from 'src/layout/parts/create-entity-input';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from 'src/library/components/section-field';
import type { ActionInput } from 'src/library/elements/action-input';
import { styleMap } from 'lit/directives/style-map.js';
import { iconUrl } from 'src/store/utilities';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-collection-view')
export class DCollectionView extends EntityContent<CollectionView, CollectionViewEditItem> {
  static readonly styles = EntityContent.styles.concat(css`
    .filter-settings {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 6px;
      padding: 2px 0 4px 0;
    }

    d-list-section[bordered] {
      margin-bottom: 0;
    }

    .subitems {
      padding-left: var(--listPaddingLeft);
    }

    .edit-list {
      background-color: var(--backgroundLightGray);
      box-shadow: var(--inputElementShadow);
      padding: 0 16px;
    }

    .documents > div {
      display: block;
      background-size: 24px;
      background-position: 0 4px;
      background-repeat: no-repeat;
      padding: 6px 0 6px 30px;
      color: var(--linkColorGray);
      cursor: pointer;
    }

    .suggestions-section {
      position: relative;
      margin-top: 16px;
      border: 1px solid var(--borderColor);
      border-radius: 20px;
      padding: 0 20px;
      background: hsl(1, 0%, 96%);
      transition: all 0.3s;
    }

    .suggestions-section[open] {
      border-radius: 12px;
      padding: 8px 20px 0 16px;
    }

    .suggestions-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      position: sticky;
      margin-left: -20px;
      margin-bottom: 2px;
      border-radius: 20px;
      padding: 8px 8px 6px 32px;
      background: hsl(1, 0%, 96%) url(/images/suggestion-gray.svg) -2px 0 no-repeat;
      background-size: 38px auto;
      color: var(--linkColorGray);
      z-index: 1;
      cursor: pointer;
      transition:
        all 0.3s,
        color 0s;
    }

    @media (hover: hover) {
      .suggestions-header:hover {
        color: black;
      }
    }

    .suggestions-section[open] .suggestions-header {
      margin-left: -6px;
      border-radius: 0;
      background-size: 50px;
      background-position-x: -5px;
      background-position-y: -7px;
      padding-left: 40px;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-color);
    }

    .search-section {
      position: relative;
      margin-top: 12px;
      border-top: 1px solid var(--borderColor);
    }

    .search-header {
      position: sticky;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      padding: 12px 0 8px;
      background: white;
    }

    d-list-section-item {
      position: relative;
    }

    @media (hover: hover) {
      .documents > div:hover {
        color: black;
      }
    }
  `);
  @property({ type: String })
  entityType = 'collections';
  @property({ type: String })
  filterYearSelected = '';
  @property({ type: String })
  filterAssignee = '';
  @state()
  searchString = '';
  @state()
  docTypeFilter = 'all';
  @state()
  showSuggestions = false;

  protected get preventDelete(): boolean {
    return this.entityView.editRestricted;
  }

  private get filterAssignees(): string[] {
    if (!this.filterAssignee) {
      return [];
    }
    const partner = this.entityView.partnersWithContacts.find((p) => {
      return p.uuid === this.filterAssignee;
    });
    if (partner) {
      return partner.contacts.map((c) => {
        return c.uuid;
      });
    }
    return [this.filterAssignee];
  }

  private get filterAssigneeSelectValue(): string {
    if (this.entityView.filterPeople.length === 0) {
      return '';
    }
    const selected = this.entityView.filterByAssignedOptions.filter((o) => {
      return this.entityView.filterPeople?.includes(o.value);
    });
    if (selected.length) {
      if (
        selected.length > 1 &&
        selected[0].partnerUuid &&
        allEqual(
          selected.map((o) => {
            return o.partnerUuid;
          }),
        )
      ) {
        return selected[0].partnerUuid;
      }
      return selected[0].value;
    }
    return '';
  }

  private get collectedIds(): string[] {
    return this.entityView.items
      .filter((item) => {
        return item.uuid !== '';
      })
      .map((item) => {
        return item.uuid;
      });
  }

  private get docTypes(): SelectDropdownOption[] {
    return [
      { value: 'events', text: t('collections.options.events') },
      { value: 'meetings', text: t('collections.options.meetings') },
      { value: 'reports', text: t('collections.options.reports') },
      { value: 'tasks', text: t('collections.options.tasks') },
      { value: 'guidelines', text: t('collections.options.guidelines') },
      { value: 'documents', text: t('collections.options.documents') },
      { value: 'constitutionalDocuments', text: t('collections.options.constitutionalDocuments') },
      { value: 'contracts', text: t('collections.options.contracts') },
      { value: 'employees', text: t('collections.options.employees') },
      { value: 'partners', text: t('collections.options.partners') },
      { value: 'contacts', text: t('collections.options.contacts') },
      { value: 'functions', text: t('collections.options.functions') },
      { value: 'assets', text: t('collections.options.assets') },
      { value: 'substances', text: t('collections.options.substances') },
      { value: 'riskAssessments', text: t('collections.options.riskAssessments') },
      { value: 'issues', text: t('collections.options.issues') },
    ];
  }

  private get docTypeOptions(): SelectDropdownOption[] {
    const options = [...this.docTypes];
    options.unshift({ value: 'all', text: t('collections.options.all') });
    return options;
  }

  private get filteredEventsCount() {
    return this.entityView.eventsUnfilteredCount - this.entityView.events.length;
  }

  private get uncollected() {
    return this.entityView.collectibles.filter((item) => {
      let uuid = item.uuid;
      if (item.interviewEmployee) {
        uuid = item.uuid + '?employee=' + item.interviewEmployee;
      }
      return !this.collectedIds.includes(uuid);
    });
  }

  private get searchResults(): CollectionEntity[] {
    if (this.docTypeFilter === 'all') {
      if (this.searchString === '') {
        return [];
      }
      return this.uncollected.filter((item) => {
        return item.name.toUpperCase().includes(this.searchString.toUpperCase());
      });
    }
    if (this.searchString === '') {
      return this.uncollected.filter((item) => {
        return item.type === this.docTypeFilter;
      });
    }
    return this.uncollected.filter((item) => {
      return item.type === this.docTypeFilter && item.name.toUpperCase().includes(this.searchString.toUpperCase());
    });
  }

  private get suggestionsCount(): number {
    let count = this.entityView.suggestionsAsListItems.length;
    this.entityView.suggestionsAsListItems.forEach((item) => {
      if (item.items) {
        count += item.items.length;
      }
    });
    return count;
  }

  hideEdit() {
    return this.entityView.editRestricted;
  }

  renderHelpSection() {
    if (this._normalMode(this.mode)) {
      return super.renderHelpSection();
    }
    return html``;
  }

  async initializeEditItem() {
    this.editItem = {
      name: this.entityView.name,
      description: this.entityView.description,
      pages: this.entityView.pages,
      items: this.entityView.items,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  /**
   * Update entityView before save to ensure quick response
   */
  updateEntityView(items: CollectionItem[]) {
    const entities: CollectionEntity[] = [];
    items.forEach((item) => {
      const entity = this.entityView.collectibles.find((c) => {
        return c.uuid === item.uuid;
      });
      if (entity) {
        entities.push(entity);
      }
    });
    this.entityView = {
      ...this.entityView,
      items,
      events: getEventItemsList(
        entities.filter((e) => {
          return e.type === 'events' || e.type === 'meetings';
        }),
        this.entityView.filterOn,
        this.entityView.filterStartDate,
        this.entityView.filterEndDate,
        this.entityView.filterPeople,
        this.entityView.href,
        'shortNames',
      ),
      eventsFullNames: getEventItemsList(
        entities.filter((e) => {
          return e.type === 'events' || e.type === 'meetings';
        }),
        this.entityView.filterOn,
        this.entityView.filterStartDate,
        this.entityView.filterEndDate,
        this.entityView.filterPeople,
        this.entityView.href,
        'fullNames',
      ),
      reports: getItemsList('reports', entities, this.entityView.href),
      tasks: getItemsList('tasks', entities, this.entityView.href),
      guidelines: getItemsList('guidelines', entities, this.entityView.href),
      documents: getItemsList('documents', entities, this.entityView.href),
      constitutionalDocuments: getItemsList('constitutionalDocuments', entities, this.entityView.href),
      contracts: getItemsList('contracts', entities, this.entityView.href),
      employees: getItemsList('employees', entities, this.entityView.href),
      partners: getItemsList('partners', entities, this.entityView.href),
      contacts: getItemsList('contacts', entities, this.entityView.href),
      functions: getItemsList('functions', entities, this.entityView.href),
      assets: getItemsList('assets', entities, this.entityView.href),
      substances: getItemsList('substances', entities, this.entityView.href),
      riskAssessments: getItemsList('riskAssessments', entities, this.entityView.href),
      issues: getItemsList('issues', entities, this.entityView.href),
    };
  }

  addItems(entities: CollectionEntity[]) {
    const items: CollectionEntity[] = getItemsAsCollectionEntities(this.entityView.items, this.entityView.collectibles);
    entities.forEach((e) => {
      let interviewEmployee: string | undefined = undefined;
      if (e.templateId === employeeInterviewTemplateId) {
        interviewEmployee = e.assigneeUuids[0];
      }
      e.interviewEmployee = interviewEmployee;
      items.push(e);
    });
    const itemsToSave: CollectionItem[] = items.map((i) => {
      return {
        uuid: i.uuid,
        type: i.type,
        interviewEmployee: i.interviewEmployee,
        createEvent: i.createEvent,
      };
    });
    this.saveItems(itemsToSave);
  }

  saveItems(items: CollectionItem[]) {
    this.updateEntityView(items);
    this.dispatchEvent(
      new CustomEvent('done', {
        bubbles: true,
        composed: true,
        detail: {
          editItem: {
            name: this.entityView.name,
            helpContent: this.entityView.helpContent,
            description: this.entityView.description,
            pages: this.entityView.pages,
            items: items,
            editRestricted: this.entityView.editRestricted,
          },
        },
      }),
    );
  }

  searchResultDocTypeStyle(docType: string) {
    return {
      backgroundImage: iconUrl(docType, 'gray'),
    };
  }

  renderSuggestionItem(item: { listItem: ListSectionItemInput; subitems: ListSectionItemInput[] }) {
    let popoverCreate = '';
    let popoverInclude = '';
    if (item.listItem.clickData) {
      const clickData: CollectionSuggestionClickData = JSON.parse(item.listItem.clickData);
      popoverCreate = this.suggestionPopoverCreate(clickData, item.listItem.accessible);
      popoverInclude = this.suggestionPopoverInclude(clickData);
    }
    return html`
      <d-list-section-item
        style="position: relative"
        bordered
        .input=${item.listItem}
        data-popover="${popoverCreate}"
        data-popover-background="green"
      ></d-list-section-item>
      ${item.subitems.length
        ? html` <div class="subitems">
            ${item.subitems.map((subitem) => {
              return html` <d-list-section-item
                bordered
                .input=${subitem}
                data-popover="${popoverInclude}"
                data-popover-background="blue"
              ></d-list-section-item>`;
            })}
          </div>`
        : nothing}
    `;
  }

  renderSuggestions() {
    if (this.entityView.suggestionsAsListItems.length) {
      const items: { listItem: ListSectionItemInput; subitems: ListSectionItemInput[] }[] =
        this.entityView.suggestionsAsListItems.map((item) => {
          item.clickHandler = () => this.onSuggestionClick(item);
          let subitems: ListSectionItemInput[] = [];
          if (item.items) {
            subitems = item.items.map((subitem) => {
              subitem.clickHandler = () => this.onSuggestionEventClick(subitem);
              return subitem;
            });
          }
          return { listItem: { ...item, items: undefined }, subitems };
        });
      return html`
        <div class="suggestions-section" ?open="${this.showSuggestions}">
          <div
            class="suggestions-header"
            style="top: ${this.contentStickyTop - 2}px"
            @click=${() => (this.showSuggestions = !this.showSuggestions)}
          >
            ${this.suggestionsCount
              ? html` ${t('collections.contentSuggestionsCount', { count: this.suggestionsCount })} `
              : nothing}
            ${this.showSuggestions ? html` <d-closer style="top: 0; right: -10px"></d-closer> ` : nothing}
          </div>
          <d-smooth-resize>
            <d-popover-bubble offset="-4">
              ${this.showSuggestions
                ? html` ${items.map((item) => {
                    return this.renderSuggestionItem(item);
                  })}`
                : nothing}
            </d-popover-bubble>
          </d-smooth-resize>
        </div>
      `;
    }
    return html``;
  }

  renderSearch() {
    return html`
      <div class="search-section">
        <div class="search-header" style="top: ${this.contentStickyTop - 2}px">
          <d-edit-text
            type="search"
            system-content
            theme-page
            placeholder=${t('collections.searchPlaceholder')}
            style="flex:2; min-width: 300px"
            .value=${this.searchString}
            @value-changed=${(e) => {
              e.stopPropagation();
              this.searchString = e.detail.value;
            }}
          ></d-edit-text>
          <d-select-dropdown
            style="flex:1; min-width: 200px"
            system-content
            theme-page
            .options=${this.docTypeOptions}
            .value=${this.docTypeFilter}
            @value-changed=${(e) => {
              e.stopPropagation();
              this.docTypeFilter = e.detail.value;
            }}
          ></d-select-dropdown>
        </div>
        <d-smooth-resize>
          <div class="documents">
            ${this.searchResults.map((item) => {
              return html` <div
                class="${item.classes}"
                style=${styleMap(this.searchResultDocTypeStyle(item.type))}
                @click=${() => this.addToCollection(item.uuid)}
              >
                ${item.dateTimeDisplay ? item.dateTimeDisplay + ' ' + item.name : item.name}
              </div>`;
            })}
          </div>
        </d-smooth-resize>
      </div>
    `;
  }

  private get hiddenItemsText(): string {
    let text = '';
    if (this.entityView.filterOn) {
      text = t('collections.hiddenItemsText', {
        count: this.filteredEventsCount,
        numberWord: capitalize(numberWord(this.filteredEventsCount, true)),
      });
    }
    return text;
  }

  renderEventsList() {
    return html`
      <d-smooth-resize style="margin-right: -20px; padding-right: 20px">
        ${this.entityView.eventsUnfilteredCount > 0
          ? html` <d-list-header icon="events" field="collections_events" style="margin-bottom: 6px">
                <div slot="top-right">
                  <span style="font-size: 13px; font-weight: 200">${this.hiddenItemsText}</span>
                  <d-action plain @click=${() => this.filterOnChanged()}
                    >${this.entityView.filterOn ? t('collections.turnOffFilter') : t('collections.setFilter')}
                  </d-action>
                </div>
                <div slot="bottom">
                  <d-smooth-resize>
                    ${this.entityView.filterOn
                      ? html` <div class="filter-settings">
                          <d-edit-timespan
                            semibold-label
                            style="margin-right: 4px"
                            .start=${this.entityView.filterStartDate}
                            .end=${this.entityView.filterEndDate}
                            .text=${this.entityView.filterPeriodDisplayText}
                            @value-changed=${(e) => this.filterTimespanChanged(e)}
                          ></d-edit-timespan>
                          <d-select-dropdown
                            controller
                            semibold-label
                            label=${t('collections.participants')}
                            inline-label
                            .options=${this.entityView.filterByAssignedOptions}
                            .value=${this.filterAssigneeSelectValue}
                            @value-changed=${(e) => this.filterAssigneeChanged(e)}
                          ></d-select-dropdown>
                        </div>`
                      : nothing}
                  </d-smooth-resize>
                </div>
              </d-list-header>
              <d-list-section
                no-header
                bordered
                delete-button
                .items=${this.entityView.events}
                @remove-item=${(e: CustomEvent) => this.onRemoveListItem(e.detail)}
              ></d-list-section>`
          : nothing}
      </d-smooth-resize>
    `;
  }

  renderNoItems() {
    if (this.entityView.entitiesUnfilteredCount === 0) {
      return html` <div style="border-top: 1px solid var(--borderColor); padding: 12px 0 3px 0; font-style: italic">
        ${t('collections.collectionHasNoItems', { name: this.entityView.name })}
      </div>`;
    }
    return nothing;
  }

  renderViewMode() {
    return html`
      ${this.renderFieldSection('collections', this.viewFields(), this.entityView)} ${this.renderNoItems()}
      ${this.renderEventsList()}
    `;
  }

  renderEditItem(item: CollectionViewEditItem): TemplateResult<1> {
    return html`${this.renderFieldSection('collections', this.editFields(), item)} `;
  }

  renderRevisionItems() {
    return html``;
  }

  hidePdf(): boolean {
    return true;
  }

  mainHeaderMoreActions(): ActionInput[] {
    return [
      ...super.mainHeaderMoreActions(),
      ...(this.mode === 'normal'
        ? [
            {
              name: t('collections.createPdfOfDocument'),
              action: 'new_pdf',
            },
          ]
        : []),
    ];
  }

  protected renderEditSelectPages(item: any): TemplateResult<1> {
    if (this.entityView.editRestricted) {
      return html``;
    }
    return super.renderEditSelectPages(item);
  }

  protected handleAction(action: string) {
    if (action === 'new_pdf') {
      const view: CollectionPdf = {
        ...this.entityView,
        events: this.entityView.eventsFullNames.map((item) => {
          return {
            checkedClass: item.secondaryLabel !== '' ? 'checked' : '',
            name: item.label ?? '',
            assignedTo: item.rightLabel,
            doneText: item.secondaryLabel ?? '',
            notes: item.notes ?? '',
          };
        }),
        reports: this.entityView.reports.map((x) => ({ name: x.label ?? '' })),
        tasks: this.entityView.tasks.map((x) => ({ name: x.label ?? '' })),
        guidelines: this.entityView.guidelines.map((x) => ({ name: x.label ?? '' })),
        documents: this.entityView.documents.map((x) => ({ name: x.label ?? '' })),
        constitutionalDocuments: this.entityView.constitutionalDocuments.map((x) => ({ name: x.label ?? '' })),
        contracts: this.entityView.contracts.map((x) => ({ name: x.label ?? '' })),
        functions: this.entityView.functions.map((x) => ({ name: x.label ?? '' })),
        assets: this.entityView.assets.map((x) => ({ name: x.label ?? '' })),
        substances: this.entityView.substances.map((x) => ({ name: x.label ?? '' })),
        riskAssessments: this.entityView.riskAssessments.map((x) => ({ name: x.label ?? '' })),
        issues: this.entityView.issues.map((x) => ({ name: x.label ?? '' })),
        employees: this.entityView.employees.map((x) => ({ name: x.label ?? '' })),
        partners: this.entityView.partners.map((x) => ({ name: x.label ?? '' })),
        contacts: this.entityView.contacts.map((x) => ({ name: x.label ?? '' })),
      };

      // pdfmake (inkl. innbakte fonter, ~4 MB) lastes ved første bruk (#13442).
      void import('src/pdf/make-pdf').then(({ createTopicPdf }) => createTopicPdf(view).download());
    }
  }

  protected onRemoveListItem(detail: ListSectionItemInput) {
    if (detail.href && detail.label) {
      const hrefParts = detail.href.split('/');
      const uuid = hrefParts[hrefParts.length - 1];
      const entity = this.entityView.collectibles.find((c) => {
        return c.uuid === uuid;
      });
      if (entity) {
        if (entity.type === 'events' || entity.type === 'meetings') {
          const sameNameEvents = this.entityView.collectibles.filter((item) => {
            return (
              item.type === entity.type &&
              item.name === entity.name &&
              item.interviewEmployee === entity.interviewEmployee &&
              this.collectedIds.includes(item.uuid)
            );
          });
          if (sameNameEvents.length > 1) {
            return this.recurringEventsDialog(entity, sameNameEvents, true);
          } else {
            this.removeFromCollection([entity]);
          }
        } else {
          this.removeFromCollection([entity]);
        }
      }
    }
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'description',
        type: 'view-text',
        class: 'fullWidth',
        condition: !isEmptyOrInvalidString(this.entityView.description),
      },
      {
        field: 'filterPeriodDisplayText',
        type: 'view-text',
        class: 'minWidth200',
        condition: false,
      },
      {
        field: 'filterPeopleDisplayText',
        type: 'view-text',
        class: 'minWidth200',
        condition: false,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'collections_events',
        icon: 'events',
        smoothResize: true,
        items: 'events',
        deleteButton: true,
        condition: false,
      },
      {
        field: 'organization_reports',
        icon: 'reports',
        smoothResize: true,
        items: 'reports',
        deleteButton: true,
        condition: this.entityView.reports.length > 0,
      },
      {
        field: 'organization_tasks',
        icon: 'tasks',
        items: 'tasks',
        deleteButton: true,
        condition: this.entityView.tasks.length > 0,
      },
      {
        field: 'organization_guidelines',
        icon: 'guidelines',
        smoothResize: true,
        items: 'guidelines',
        deleteButton: true,
        condition: this.entityView.guidelines.length > 0,
      },
      {
        field: 'organization_documents',
        icon: 'documents',
        smoothResize: true,
        items: 'documents',
        deleteButton: true,
        condition: this.entityView.documents.length > 0,
      },
      {
        field: 'organization_constitutionalDocuments',
        icon: 'constitutionalDocuments',
        smoothResize: true,
        items: 'constitutionalDocuments',
        condition: this.entityView.constitutionalDocuments.length > 0,
      },
      {
        field: 'organization_contracts',
        icon: 'contracts',
        smoothResize: true,
        items: 'contracts',
        deleteButton: true,
        condition: this.entityView.contracts.length > 0,
      },
      {
        field: 'organization_functions',
        icon: 'functions',
        smoothResize: true,
        items: 'functions',
        deleteButton: true,
        condition: this.entityView.functions.length > 0,
      },
      {
        field: 'organization_assets',
        icon: 'assets',
        smoothResize: true,
        items: 'assets',
        deleteButton: true,
        condition: this.entityView.assets.length > 0,
      },
      {
        field: 'organization_substances',
        icon: 'substances',
        smoothResize: true,
        items: 'substances',
        deleteButton: true,
        condition: this.entityView.substances.length > 0,
      },
      {
        field: 'organization_riskAssessments',
        icon: 'riskAssessments',
        smoothResize: true,
        items: 'riskAssessments',
        deleteButton: true,
        condition: this.entityView.riskAssessments.length > 0,
      },
      {
        field: 'organization_issues',
        icon: 'issues',
        smoothResize: true,
        items: 'issues',
        deleteButton: true,
        condition: this.entityView.issues.length > 0,
      },
      {
        field: 'organization_employees',
        icon: 'employees',
        smoothResize: true,
        items: 'employees',
        deleteButton: true,
        condition: this.entityView.employees.length > 0,
      },
      {
        field: 'organization_partners',
        icon: 'partners',
        smoothResize: true,
        items: 'partners',
        deleteButton: true,
        condition: this.entityView.partners.length > 0,
      },
      {
        field: 'organization_contacts',
        icon: 'contacts',
        smoothResize: true,
        items: 'contacts',
        deleteButton: true,
        condition: this.entityView.contacts.length > 0,
      },
      {
        type: 'custom',
        render: () => this.renderSuggestions(),
        condition: this.entityView.suggestionsAsListItems.length > 0,
      },
      {
        type: 'custom',
        render: () => this.renderSearch(),
        condition: !this.entityView.editRestricted,
      },
    ];
  }

  protected renderAttachments() {
    return html``;
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
  }

  protected update(changedProperties: PropertyValues) {
    super.update(changedProperties);
    this.saveCollectionsFilter();
  }

  private async recurringEventsDialog(entity: CollectionEntity, sameNameEvents: CollectionEntity[], removing: boolean) {
    const result: RecurringEventInCollectionDialogResult = await RecurringEventInCollectionDialog.open({
      collectionName: this.entityView.name,
      eventsCount: sameNameEvents.length,
      isMeeting: entity.type === 'meetings',
      removing,
    });
    if (result.action === 'cancel') {
      return;
    }
    if (result.action === 'all') {
      if (removing) {
        this.removeFromCollection(sameNameEvents);
      } else {
        this.addItems(sameNameEvents);
      }
    } else if (result.action === 'single') {
      if (removing) {
        this.removeFromCollection([entity]);
      } else {
        this.addItems([entity]);
      }
    }
  }

  private addToCollection(uuid: string) {
    const entity = this.entityView.collectibles.find((c) => {
      return c.uuid === uuid;
    });
    if (entity) {
      if (entity.type === 'events' || entity.type === 'meetings') {
        const sameNameEvents = this.entityView.collectibles.filter((item) => {
          return (
            item.type === entity.type &&
            item.name === entity.name &&
            item.interviewEmployee === entity.interviewEmployee &&
            !this.collectedIds.includes(item.uuid)
          );
        });
        if (sameNameEvents.length > 1) {
          return this.recurringEventsDialog(entity, sameNameEvents, false);
        } else {
          this.addItems([entity]);
        }
      } else {
        this.addItems([entity]);
      }
    }
  }

  private removeFromCollection(entities: CollectionEntity[]) {
    const uuids = entities.map((e) => e.uuid);
    const itemsToSave: CollectionItem[] = this.entityView.items.filter((i) => {
      return !uuids.includes(i.uuid);
    });
    this.saveItems(itemsToSave);
  }

  private filterOnChanged() {
    let filterOn = true;
    if (this.entityView.filterOn) {
      filterOn = false;
    }
    this.entityView.filterOn = filterOn;
    const params = getFilterParams(
      filterOn,
      this.entityView.filterStartDate,
      this.entityView.filterEndDate,
      this.filterAssignees,
    );
    this.dispatchEvent(
      new CustomEvent('navigate', {
        composed: true,
        bubbles: true,
        detail: { href: this.entityView.href + params },
      }),
    );
    this.saveCollectionsFilter();
  }

  private setFilter(
    filterOn: boolean | undefined,
    startDate: string | undefined,
    endDate: string | undefined,
    assignees: string[] | undefined,
  ) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        composed: true,
        bubbles: true,
        detail: { href: this.entityView.href + getFilterParams(filterOn, startDate, endDate, assignees) },
      }),
    );
  }

  private filterTimespanChanged(e) {
    e.stopPropagation();
    this.setFilter(this.entityView.filterOn, e.detail.start, e.detail.end, this.filterAssignees);
  }

  private filterAssigneeChanged(e) {
    e.stopPropagation();
    this.filterAssignee = e.detail.value;
    this.setFilter(
      this.entityView.filterOn,
      this.entityView.filterStartDate,
      this.entityView.filterEndDate,
      this.filterAssignees,
    );
  }

  private createTaskEvent(taskUuid: string) {
    const id = uuid();
    const input: CreateEventOccurrenceInput = {
      entityType: 'eventOccurrences',
      entityUuid: id,
      targetUrl: this.entityView.href + '/eventOccurrences/' + id + '?edit',
      taskUuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private createMeetingEvent(clickData) {
    if (this.entityView.currentUserHasWriteAccess) {
      const id = uuid();
      const input: CreateEntityInput = {
        entityType: 'meetingOccurrences',
        entityUuid: id,
        meetingUuid: clickData.uuid,
        employeeUuid: clickData.interviewEmployee,
        templateMeetingOccurrenceUuid: undefined,
        instance: LocalDate.now().toString(),
        targetUrl: this.entityView.href + '/meetingOccurrences/' + id + '?edit',
      };
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: input,
        }),
      );
    }
  }

  private onSuggestionClick(item: ListSectionItemInput) {
    if (item.clickData) {
      const clickData = JSON.parse(item.clickData);
      if (clickData.createEvent) {
        if (clickData.type === 'meetings') {
          this.createMeetingEvent(clickData);
        } else {
          this.createTaskEvent(clickData.uuid);
        }
      }
    }
  }

  private onSuggestionEventClick(item: ListSectionItemInput) {
    if (item.clickData) {
      const clickData = JSON.parse(item.clickData);
      return this.addToCollection(clickData.uuid);
    }
  }

  private suggestionPopoverCreate(clickData: CollectionSuggestionClickData, accessible: boolean) {
    if (accessible) {
      if (clickData.type === 'meetings') {
        if (clickData.interviewEmployee) {
          return t('collections.createNewConversation');
        } else {
          return t('collections.createNewMeeting');
        }
      } else {
        return t('collections.createNewTaskFromRoutine');
      }
    }
    return '';
  }

  private suggestionPopoverInclude(clickData: CollectionSuggestionClickData) {
    if (clickData.type === 'meetings') {
      if (clickData.interviewEmployee) {
        return t('collections.includeThisConversation');
      } else {
        return t('collections.includeThisMeeting');
      }
    } else {
      return t('collections.includeThisTask');
    }
  }

  private editFields(): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        placeholder: t('collections.fieldMustBeFilledOut'),
        autofocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        class: 'fullWidth',
      },
      {
        field: 'description',
        type: 'edit-textarea',
        class: 'fullWidth',
      },
    ];
  }

  private saveCollectionsFilter() {
    let filterChanged = false;
    const newCollectionFilter: CollectionFilter = {
      uuid: this.entityView.uuid,
      filterOn: this.entityView.filterOn,
      startDate: this.entityView.filterStartDate ?? '',
      endDate: this.entityView.filterEndDate ?? '',
      people: this.entityView.filterPeople ?? [],
    };
    let newAllCollectionsFilter: CollectionFilter[] = [...this.entityView.allCollectionsFilter];
    const currentFilter = this.entityView.allCollectionsFilter.find((c) => c.uuid === this.entityView.uuid);
    if (currentFilter) {
      if (
        currentFilter.filterOn !== this.entityView.filterOn ||
        currentFilter.startDate !== this.entityView.filterStartDate ||
        currentFilter.endDate !== this.entityView.filterEndDate ||
        !_.isEqual(currentFilter.people, this.entityView.filterPeople)
      ) {
        filterChanged = true;
        newAllCollectionsFilter = this.entityView.allCollectionsFilter.map((c) => {
          if (c.uuid === this.entityView.uuid) {
            return newCollectionFilter;
          }
          return c;
        });
      }
    } else {
      filterChanged = true;
      newAllCollectionsFilter.push(newCollectionFilter);
    }
    if (filterChanged) {
      this.dispatchEvent(
        new CustomEvent('update-collections-filter', {
          composed: true,
          bubbles: true,
          detail: newAllCollectionsFilter,
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-collection-view': DCollectionView;
  }
}
